import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Leaf, Droplets, Wind, Sun, Heart, Flower2, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ZenRetreat = () => {
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: '1'
  });

  const handleBooking = (e) => {
    e.preventDefault();
    alert('This is a demo site. Booking functionality is not active.');
  };

  const treatments = [
    { name: "Hot Stone Massage", duration: "90 min", price: "CHF 220" },
    { name: "Alpine Herbal Wrap", duration: "60 min", price: "CHF 180" },
    { name: "Deep Tissue Therapy", duration: "75 min", price: "CHF 195" },
    { name: "Couples Retreat", duration: "120 min", price: "CHF 450" }
  ];

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 bg-teal-700 text-white p-3 rounded-full shadow-lg hover:bg-teal-800 transition-all duration-300 hover:scale-110"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      {/* Hero */}
      <header className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1920&q=80"
            alt="Zen Spa"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-teal-900/40 via-transparent to-stone-100"></div>
        </div>

        <div className="relative h-full flex flex-col">
          <nav className="container mx-auto px-6 py-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Leaf className="h-10 w-10 text-teal-300" />
              <span className="text-2xl font-light text-white tracking-wide">Zen Retreat</span>
            </div>
            <div className="hidden md:flex items-center gap-8 text-white/90 text-sm font-light">
              <button onClick={() => document.getElementById('philosophy').scrollIntoView({ behavior: 'smooth' })} className="hover:text-teal-300">Philosophy</button>
              <button onClick={() => document.getElementById('spa').scrollIntoView({ behavior: 'smooth' })} className="hover:text-teal-300">Spa</button>
              <button onClick={() => document.getElementById('rooms').scrollIntoView({ behavior: 'smooth' })} className="hover:text-teal-300">Rooms</button>
              <button onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })} className="hover:text-teal-300">Book</button>
            </div>
          </nav>

          <div className="flex-1 flex items-center justify-center text-center px-6">
            <div className="max-w-3xl">
              <p className="text-teal-300 tracking-[0.3em] uppercase text-sm mb-8 font-light">Wellness Sanctuary</p>
              <h1 className="text-6xl md:text-8xl font-extralight text-white mb-8 leading-tight">
                Find Your<br />
                <span className="italic text-teal-200">Inner Peace</span>
              </h1>
              <p className="text-xl text-white/80 font-light">
                A holistic wellness destination where ancient healing traditions meet modern luxury
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Philosophy Section */}
      <section id="philosophy" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Flower2 className="h-12 w-12 text-teal-600 mx-auto mb-8" />
            <h2 className="text-4xl font-light text-stone-800 mb-8">Our Philosophy</h2>
            <p className="text-stone-600 leading-relaxed text-lg font-light mb-12">
              At Zen Retreat, we believe wellness is a journey, not a destination. Our approach combines 
              traditional Swiss thermal therapies with Eastern mindfulness practices, creating a sanctuary 
              where body, mind, and spirit find harmony.
            </p>
            
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Droplets, name: "Hydrotherapy", desc: "Healing waters" },
                { icon: Wind, name: "Breathwork", desc: "Mindful breathing" },
                { icon: Sun, name: "Light Therapy", desc: "Natural energy" },
                { icon: Heart, name: "Meditation", desc: "Inner stillness" }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-teal-50 rounded-full flex items-center justify-center">
                    <item.icon className="h-8 w-8 text-teal-600" />
                  </div>
                  <h3 className="font-medium text-stone-800 mb-1">{item.name}</h3>
                  <p className="text-stone-500 text-sm font-light">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Spa Section */}
      <section id="spa" className="py-24 bg-teal-800 text-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-teal-300 tracking-widest uppercase text-sm mb-4 font-light">The Spa</p>
              <h2 className="text-5xl font-light mb-6">Signature Treatments</h2>
              <p className="text-teal-100/80 font-light mb-8 leading-relaxed">
                Our expert therapists use organic alpine herbs and essential oils in treatments 
                designed to restore balance and promote deep relaxation.
              </p>
              
              <div className="space-y-4">
                {treatments.map((treatment, i) => (
                  <div key={i} className="flex justify-between items-center py-4 border-b border-teal-700/50">
                    <div>
                      <h3 className="text-lg">{treatment.name}</h3>
                      <p className="text-teal-300/70 text-sm">{treatment.duration}</p>
                    </div>
                    <span className="text-teal-300">{treatment.price}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="h-[500px] overflow-hidden rounded-sm">
              <img 
                src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80" 
                alt="Spa treatment" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Rooms Section */}
      <section id="rooms" className="py-24 bg-stone-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-teal-700 tracking-widest uppercase text-sm mb-4 font-light">Rest & Renewal</p>
            <h2 className="text-5xl font-light text-stone-800">Tranquil Accommodations</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Garden Suite", price: "CHF 380", image: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=80", desc: "Private garden, outdoor tub" },
              { name: "Mountain View Room", price: "CHF 320", image: "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=600&q=80", desc: "Panoramic alpine views" },
              { name: "Wellness Penthouse", price: "CHF 580", image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80", desc: "Private sauna, meditation deck" }
            ].map((room, i) => (
              <div key={i} className="group bg-white overflow-hidden">
                <div className="relative h-64 overflow-hidden">
                  <img src={room.image} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-light text-stone-800 mb-2">{room.name}</h3>
                  <p className="text-stone-500 text-sm mb-4 font-light">{room.desc}</p>
                  <p className="text-teal-700">From {room.price}<span className="text-stone-500 text-sm"> / night</span></p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-24 bg-teal-900 text-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-teal-300 tracking-widest uppercase text-sm mb-4 font-light">Begin Your Journey</p>
            <h2 className="text-5xl font-light">Book Your Retreat</h2>
          </div>

          <form onSubmit={handleBooking} className="bg-teal-800/50 p-10 rounded-sm">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div>
                <Label className="text-teal-200 mb-2 block text-sm font-light">Arrival</Label>
                <Input type="date" value={bookingData.checkIn} onChange={(e) => setBookingData({...bookingData, checkIn: e.target.value})} className="bg-teal-800/50 border-teal-600/50 text-white h-12" required />
              </div>
              <div>
                <Label className="text-teal-200 mb-2 block text-sm font-light">Departure</Label>
                <Input type="date" value={bookingData.checkOut} onChange={(e) => setBookingData({...bookingData, checkOut: e.target.value})} className="bg-teal-800/50 border-teal-600/50 text-white h-12" required />
              </div>
              <div>
                <Label className="text-teal-200 mb-2 block text-sm font-light">Guests</Label>
                <select value={bookingData.guests} onChange={(e) => setBookingData({...bookingData, guests: e.target.value})} className="w-full h-12 px-3 bg-teal-800/50 border border-teal-600/50 text-white rounded-md">
                  {[1,2,3,4].map(n => <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
            </div>
            <Button type="submit" className="w-full bg-teal-500 hover:bg-teal-400 text-white py-6 text-lg font-light">
              Check Availability
            </Button>
            <p className="text-center text-teal-400/50 text-xs mt-4 italic font-light">* Demo site - booking not functional</p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-stone-900">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center text-stone-400 mb-8">
            <div><MapPin className="h-6 w-6 mx-auto mb-2" /><p className="font-light">Gstaad, Switzerland</p></div>
            <div><Phone className="h-6 w-6 mx-auto mb-2" /><p className="font-light">+41 33 748 0000</p></div>
            <div><Mail className="h-6 w-6 mx-auto mb-2" /><p className="font-light">namaste@zenretreat.ch</p></div>
          </div>
          <p className="text-center text-stone-600 text-sm font-light">© 2026 Zen Retreat • Demo Site</p>
        </div>
      </footer>
    </div>
  );
};

export default ZenRetreat;
